import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Input, Row, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import FormCard from '../../shared/components/custom/FormCard';
import { formatDate } from '../../util/date-and-time';
import {
  formatFee,
  hasMaxTwoDecimals,
  krToOre,
  parseDecimal,
} from '../transactions/connect';
import { errorText, p } from './accounting';

const EMPTY = {
  teamId: '',
  feePercent: '',
  feeFixed: '',
  validFrom: '',
  note: '',
};

// The cut agreed with each club for auctions not paid through Stripe Connect,
// from a date on: a new agreement starts a new rate, earlier months keep
// theirs.
const AccountingAgreements = ({ clubNames }) => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      setAgreements((await Api.accounting.getAgreements()) || []);
    } catch (err) {
      toast.error(errorText(err, 'loadERROR'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreements();
  }, []);

  const clubName = (teamId) => clubNames[`${teamId}`] || `#${teamId}`;
  const set = (field) => (e) =>
    setForm((current) => ({ ...current, [field]: e.target.value }));

  const percent = parseDecimal(form.feePercent);
  const fixed = form.feeFixed ? parseDecimal(form.feeFixed) : 0;
  const valid =
    form.teamId &&
    form.validFrom &&
    form.feePercent !== '' &&
    !Number.isNaN(percent) &&
    percent >= 0 &&
    percent <= 100 &&
    hasMaxTwoDecimals(form.feePercent) &&
    !Number.isNaN(fixed) &&
    fixed >= 0;

  const add = async () => {
    setLoading(true);
    try {
      await Api.accounting.addAgreement({
        teamId: form.teamId,
        feePercent: percent,
        feeFixedAmount: form.feeFixed ? krToOre(form.feeFixed) : 0,
        validFrom: form.validFrom,
        ...(form.note.trim() && { note: form.note.trim() }),
      });
      toast.success(p('agreementSaved'));
      setForm(EMPTY);
      await fetchAgreements();
    } catch (err) {
      toast.error(errorText(err, 'saveERROR'));
      setLoading(false);
    }
  };

  const remove = async (id) => {
    setLoading(true);
    try {
      await Api.accounting.removeAgreement(id);
      toast.success(p('agreementRemoved'));
      await fetchAgreements();
    } catch (err) {
      toast.error(errorText(err, 'saveERROR'));
      setLoading(false);
    }
  };

  const clubs = Object.entries(clubNames).sort(([, a], [, b]) =>
    `${a}`.localeCompare(`${b}`)
  );

  return (
    <>
      <Loading loading={loading} />
      <p className="mb-3">{p('agreementsIntro')}</p>
      <FormCard title={p('addAgreement')} className="mb-3">
        <Row form>
          <Col md={3}>
            <Input
              type="select"
              bsSize="sm"
              aria-label={p('club')}
              value={form.teamId}
              onChange={set('teamId')}
            >
              <option value="">{p('chooseClub')}</option>
              {clubs.map(([id, name]) => (
                <option key={id} value={id}>
                  {name || `#${id}`}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={2}>
            <Input
              bsSize="sm"
              inputMode="decimal"
              placeholder={p('feePercent')}
              aria-label={p('feePercent')}
              value={form.feePercent}
              onChange={set('feePercent')}
            />
          </Col>
          <Col md={2}>
            <Input
              bsSize="sm"
              inputMode="decimal"
              placeholder={p('feeFixed')}
              aria-label={p('feeFixed')}
              value={form.feeFixed}
              onChange={set('feeFixed')}
            />
          </Col>
          <Col md={2}>
            <Input
              type="date"
              bsSize="sm"
              aria-label={p('validFrom')}
              value={form.validFrom}
              onChange={set('validFrom')}
            />
          </Col>
          <Col md={2}>
            <Input
              bsSize="sm"
              placeholder={p('note')}
              aria-label={p('note')}
              value={form.note}
              onChange={set('note')}
            />
          </Col>
          <Col md={1}>
            <Button color="primary" size="sm" disabled={!valid} onClick={add}>
              {p('save')}
            </Button>
          </Col>
        </Row>
      </FormCard>
      {agreements.length === 0 ? (
        <p className="text-muted">{p('noAgreements')}</p>
      ) : (
        <FormCard bodyClassName="form-card__body--flush">
          <Table responsive striped className="table--roomy table--stack mb-0">
            <thead>
              <tr>
                <th>{p('club')}</th>
                <th>{p('cutOwed')}</th>
                <th>{p('validFrom')}</th>
                <th>{p('note')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {agreements.map((agreement) => (
                <tr key={agreement.id}>
                  <td>{clubName(agreement.teamId)}</td>
                  <td>
                    {formatFee(agreement.feePercent, agreement.feeFixedAmount)}
                  </td>
                  <td>{formatDate(agreement.validFrom)}</td>
                  <td>{agreement.note || ''}</td>
                  <td>
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      className="mb-0"
                      onClick={() => remove(agreement.id)}
                    >
                      {p('remove')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </FormCard>
      )}
    </>
  );
};

AccountingAgreements.propTypes = {
  clubNames: PropTypes.objectOf(PropTypes.string),
};

AccountingAgreements.defaultProps = {
  clubNames: {},
};

export default AccountingAgreements;
